import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DngCheckout, InitOptions } from '@domgen/checkout-sdk-types';
import { determineIsDesktop } from '@domgen/dgx-fe-common';
import { DOMAIN_CONFIG, DomainConfig } from '@domgen/dgx-fe-config';
import { ErrorRoutes } from '@error/util-models';
import { RegistrationFacade } from '@us-registration/data-access';
import {
  CheckoutEventFailure,
  CheckoutEventSuccess,
  CheckoutInitialisation,
  RegistrationRoutes,
} from '@us-registration/util-models';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';

interface CheckoutWindow extends Window {
  dngCheckout: DngCheckout;
}

interface ViewModel {
  firstName?: string;
  clientName: string;
  isDesktop: boolean;
  checkoutLoading: boolean;
}

@Injectable()
export class CheckoutComponentService {
  checkoutLoading$ = new BehaviorSubject<boolean>(false);
  window: CheckoutWindow = this.document.defaultView as CheckoutWindow;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly registrationFacade: RegistrationFacade,
    protected readonly http: HttpClient,
    private router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(DOMAIN_CONFIG) private readonly domainConfig: DomainConfig
  ) {}

  readonly vm$: Observable<ViewModel> = combineLatest({
    firstName: this.registrationFacade.customerFirstName$,
    clientName: of(this.domainConfig.clientName),
    isDesktop: determineIsDesktop(),
    checkoutLoading: this.checkoutLoading$,
  });

  getCheckout() {
    this.checkoutLoading$.next(true);

    this.registrationFacade.checkoutInitialisation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((checkoutInit: CheckoutInitialisation | undefined) => {
        if (checkoutInit) {
          this.initCheckout(checkoutInit);
        }
      });
  }

  checkoutSuccess(success: CheckoutEventSuccess): void {
    this.registrationFacade.addCheckoutSuccess(success);
    if (success.status === 'success') {
      this.router.navigate([RegistrationRoutes.RegistrationOrderConfirmation]);
    }
  }

  checkoutFailure(failure: CheckoutEventFailure): void {
    console.log('checkoutFailure', failure);
    this.router.navigate([ErrorRoutes.Generic]);
  }

  checkoutTimeout(): void {
    this.router.navigate([ErrorRoutes.Generic]);
  }

  checkoutError(): void {
    this.router.navigate([ErrorRoutes.Generic]);
  }

  private async initCheckout(checkoutInit: CheckoutInitialisation) {
    const checkout = await this.window.dngCheckout.initialise('checkout', {
      authToken: checkoutInit.authToken,
      basket: checkoutInit.basket as InitOptions['basket'],
      customer: checkoutInit.customer as InitOptions['customer'],
      proposition: checkoutInit.proposition,
    });
    checkout.renderCheckout().then(() => {
      this.checkoutLoading$.next(false);
    });
  }
}
